import { ParticipantManagerContext } from 'contexts/participant_manager_context'
import React, { useEffect, useMemo } from 'react'

import { ParticipantAudio } from '../participant_audio'
import { useAutoNavCallViews } from './use_auto_nav_call_views'
import { useLocalParticipant } from './use_local_participant'
import { useRemoteParticipants } from './use_remote_participants'
import { useRemoteParticipantsWithVideo } from './use_remote_participants_with_video'

export const ParticipantManager = ({
  room,
  passBackFromParticipantManager,
  children,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
React.PropsWithChildren<any>) => {
  const { localParticipant, localTrackData } = useLocalParticipant(room)
  const remoteParticipants = useRemoteParticipants(room)
  const remoteParticipantsWithVideo = useRemoteParticipantsWithVideo(
    remoteParticipants,
    localTrackData,
  )

  const {
    publishedAudioTrack,
    publishedVideoTrack,
    publishedScreenShareTrack,
    unpublishedAudioTrack,
    unpublishedVideoTrack,
    unpublishedScreenShareTrack,
    createLocalAudioTrack,
    createLocalVideoTrack,
    setUnpublishedAudioTrack,
    setUnpublishedVideoTrack,
  } = localTrackData

  /*
   This wraps up a lot of logic around whether there is any video active on a particular call
   remoteParticipantsWithVideo has the signature undefined | RemoteParticipants[]
   so we can do a null check here to determine if any remote participants are sharing video
  */
  const isVideoActive = useMemo(() => {
    return (
      localTrackData.isVideoEnabled ||
      localTrackData.isScreenShareEnabled ||
      !!remoteParticipantsWithVideo?.length
    )
  }, [
    localTrackData.isScreenShareEnabled,
    localTrackData.isVideoEnabled,
    remoteParticipantsWithVideo?.length,
  ])

  const autoNav = useAutoNavCallViews(isVideoActive)

  useEffect(() => {
    passBackFromParticipantManager({
      publishedAudioTrack,
      publishedVideoTrack,
      publishedScreenShareTrack,
      unpublishedAudioTrack,
      unpublishedVideoTrack,
      unpublishedScreenShareTrack,
      createLocalAudioTrack,
      createLocalVideoTrack,
      setUnpublishedAudioTrack,
      setUnpublishedVideoTrack,
    })
  }, [
    createLocalAudioTrack,
    createLocalVideoTrack,
    passBackFromParticipantManager,
    publishedAudioTrack,
    publishedScreenShareTrack,
    publishedVideoTrack,
    setUnpublishedAudioTrack,
    setUnpublishedVideoTrack,
    unpublishedAudioTrack,
    unpublishedScreenShareTrack,
    unpublishedVideoTrack,
  ])

  const contextValue = useMemo(() => {
    return {
      localParticipant,
      localTrackData,
      remoteParticipants,
      remoteParticipantsWithVideo,
      autoNav,
      isVideoActive,
    }
  }, [
    localParticipant,
    localTrackData,
    remoteParticipants,
    remoteParticipantsWithVideo,
    autoNav,
    isVideoActive,
  ])

  return (
    <ParticipantManagerContext.Provider value={contextValue}>
      {children}
      {remoteParticipants?.map((participant) => (
        <ParticipantAudio
          key={participant.identity}
          participant={participant}
        />
      ))}
    </ParticipantManagerContext.Provider>
  )
}
